import { passwordSchema } from "@postdaily/auth/lib/password-policy";
import { Badge } from "@postdaily/ui/components/badge";
import { Button } from "@postdaily/ui/components/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@postdaily/ui/components/card";
import {
  Field,
  FieldError,
  FieldGroup,
  FieldLabel,
} from "@postdaily/ui/components/field";
import { PasswordInput } from "@postdaily/ui/components/password-input";
import { Skeleton } from "@postdaily/ui/components/skeleton";
import { Spinner } from "@postdaily/ui/components/spinner";
import { useForm } from "@tanstack/react-form";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { KeyRound, Link2, Save, Unlink } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { z } from "zod";

import { PasswordRequirements } from "@/components/auth/auth-primitives";
import { PageHeader } from "@/components/page-header";
import { SessionsCard } from "@/components/settings/sessions-card";
import { TwoFactorCard } from "@/components/settings/two-factor-card";
import { getAuthCallbackUrl } from "@/lib/auth-callback-url";
import { authClient } from "@/lib/auth-client";
import { useTRPC } from "@/utils/trpc";

export const Route = createFileRoute("/(app)/settings/security")({
  component: SecuritySettingsPage,
});

const linkedAccountsQueryKey = ["auth", "listAccounts"] as const;

function useLinkedAccounts() {
  return useQuery({
    queryFn: async () => {
      const { data, error } = await authClient.listAccounts();

      if (error) {
        throw new Error(error.message || "Could not load sign-in methods.");
      }

      return data;
    },
    queryKey: linkedAccountsQueryKey,
  });
}

function SecuritySettingsPage() {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const viewer = useQuery(trpc.viewer.me.queryOptions());
  const accounts = useLinkedAccounts();

  if (viewer.isLoading || accounts.isLoading) {
    return (
      <div className="flex flex-col gap-6">
        <PageHeader
          size="lg"
          title="Security"
          description="Password, two-factor authentication, and connected sign-in methods."
        />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  if (!viewer.data) {
    return null;
  }

  const linkedAccounts = accounts.data ?? [];
  const hasPassword = linkedAccounts.some(
    (account) => account.providerId === "credential",
  );

  const refreshAccounts = () => {
    void queryClient.invalidateQueries({ queryKey: linkedAccountsQueryKey });
  };

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        size="lg"
        title="Security"
        description="Password, two-factor authentication, and connected sign-in methods."
      />
      {hasPassword ? (
        <ChangePasswordCard />
      ) : (
        <SetPasswordCard onChanged={refreshAccounts} />
      )}
      <TwoFactorCard
        accountEmail={viewer.data.user.email}
        enabled={Boolean(viewer.data.user.twoFactorEnabled)}
        requiresPassword={hasPassword}
        onChanged={() => {
          void viewer.refetch();
        }}
      />
      <SessionsCard />
      <LinkedAccountsCard
        hasPassword={hasPassword}
        linkedProviders={linkedAccounts.map((account) => account.providerId)}
        onChanged={refreshAccounts}
      />
    </div>
  );
}

const changePasswordSchema = z
  .object({
    confirmPassword: z.string(),
    currentPassword: z.string().min(1, "Enter your current password"),
    newPassword: passwordSchema,
  })
  .refine((value) => value.newPassword === value.confirmPassword, {
    message: "Passwords do not match",
    path: ["confirmPassword"],
  });

function ChangePasswordCard() {
  const form = useForm({
    defaultValues: {
      confirmPassword: "",
      currentPassword: "",
      newPassword: "",
    },
    onSubmit: async ({ value }) => {
      const { error } = await authClient.changePassword({
        currentPassword: value.currentPassword,
        newPassword: value.newPassword,
        // Kick every other device off the account after a password change.
        revokeOtherSessions: true,
      });

      if (error) {
        toast.error(
          error.message ||
            "Could not change your password. Check your current password.",
        );
        return;
      }

      form.reset();
      toast.success("Password changed. Other devices have been signed out.");
    },
    validators: {
      onSubmit: changePasswordSchema,
    },
  });

  return (
    <Card>
      <form
        onSubmit={(event) => {
          event.preventDefault();
          event.stopPropagation();
          void form.handleSubmit();
        }}
      >
        <CardHeader className="border-b">
          <CardTitle>Password</CardTitle>
          <CardDescription>
            Changing your password signs you out on every other device.
          </CardDescription>
        </CardHeader>
        <CardContent className="py-6">
          <FieldGroup className="max-w-sm">
            <form.Field name="currentPassword">
              {(field) => {
                const isInvalid =
                  field.state.meta.isTouched && !field.state.meta.isValid;

                return (
                  <Field data-invalid={isInvalid}>
                    <FieldLabel htmlFor={field.name}>
                      Current password
                    </FieldLabel>
                    <PasswordInput
                      id={field.name}
                      name={field.name}
                      aria-invalid={isInvalid}
                      autoComplete="current-password"
                      value={field.state.value}
                      onBlur={field.handleBlur}
                      onChange={(event) =>
                        field.handleChange(event.target.value)
                      }
                    />
                    {isInvalid ? (
                      <FieldError errors={field.state.meta.errors} />
                    ) : null}
                  </Field>
                );
              }}
            </form.Field>
            <form.Field name="newPassword">
              {(field) => {
                const isInvalid =
                  field.state.meta.isTouched && !field.state.meta.isValid;

                return (
                  <Field data-invalid={isInvalid}>
                    <FieldLabel htmlFor={field.name}>New password</FieldLabel>
                    <PasswordInput
                      id={field.name}
                      name={field.name}
                      aria-invalid={isInvalid}
                      autoComplete="new-password"
                      value={field.state.value}
                      onBlur={field.handleBlur}
                      onChange={(event) =>
                        field.handleChange(event.target.value)
                      }
                    />
                    <PasswordRequirements
                      password={field.state.value}
                      showErrors={isInvalid}
                    />
                    {isInvalid ? (
                      <FieldError errors={field.state.meta.errors} />
                    ) : null}
                  </Field>
                );
              }}
            </form.Field>
            <form.Field name="confirmPassword">
              {(field) => {
                const isInvalid =
                  field.state.meta.isTouched && !field.state.meta.isValid;

                return (
                  <Field data-invalid={isInvalid}>
                    <FieldLabel htmlFor={field.name}>
                      Confirm new password
                    </FieldLabel>
                    <PasswordInput
                      id={field.name}
                      name={field.name}
                      aria-invalid={isInvalid}
                      autoComplete="new-password"
                      value={field.state.value}
                      onBlur={field.handleBlur}
                      onChange={(event) =>
                        field.handleChange(event.target.value)
                      }
                    />
                    {isInvalid ? (
                      <FieldError errors={field.state.meta.errors} />
                    ) : null}
                  </Field>
                );
              }}
            </form.Field>
          </FieldGroup>
        </CardContent>
        <CardFooter>
          <form.Subscribe
            selector={(state) => ({
              canSubmit: state.canSubmit,
              isSubmitting: state.isSubmitting,
            })}
          >
            {({ canSubmit, isSubmitting }) => (
              <Button type="submit" disabled={!canSubmit || isSubmitting}>
                {isSubmitting ? (
                  <Spinner data-icon="inline-start" />
                ) : (
                  <Save data-icon="inline-start" />
                )}
                {isSubmitting ? "Saving..." : "Change password"}
              </Button>
            )}
          </form.Subscribe>
        </CardFooter>
      </form>
    </Card>
  );
}

const setPasswordSchema = z
  .object({
    confirmPassword: z.string(),
    newPassword: passwordSchema,
  })
  .refine((value) => value.newPassword === value.confirmPassword, {
    message: "Passwords do not match",
    path: ["confirmPassword"],
  });

function SetPasswordCard({ onChanged }: { onChanged: () => void }) {
  const trpc = useTRPC();
  const setPassword = useMutation(
    trpc.viewer.setPassword.mutationOptions({
      onError: (error) => {
        toast.error(error.message);
      },
      onSuccess: () => {
        toast.success("Password added. You can now also sign in with email.");
        onChanged();
      },
    }),
  );

  const form = useForm({
    defaultValues: {
      confirmPassword: "",
      newPassword: "",
    },
    onSubmit: async ({ value }) => {
      await setPassword.mutateAsync({ newPassword: value.newPassword });
      form.reset();
    },
    validators: {
      onSubmit: setPasswordSchema,
    },
  });

  return (
    <Card>
      <form
        onSubmit={(event) => {
          event.preventDefault();
          event.stopPropagation();
          void form.handleSubmit();
        }}
      >
        <CardHeader className="border-b">
          <CardTitle>Add a password</CardTitle>
          <CardDescription>
            You sign in with Google. Add a password to also sign in with your
            email address.
          </CardDescription>
        </CardHeader>
        <CardContent className="py-6">
          <FieldGroup className="max-w-sm">
            <form.Field name="newPassword">
              {(field) => {
                const isInvalid =
                  field.state.meta.isTouched && !field.state.meta.isValid;

                return (
                  <Field data-invalid={isInvalid}>
                    <FieldLabel htmlFor={field.name}>Password</FieldLabel>
                    <PasswordInput
                      id={field.name}
                      name={field.name}
                      aria-invalid={isInvalid}
                      autoComplete="new-password"
                      value={field.state.value}
                      onBlur={field.handleBlur}
                      onChange={(event) =>
                        field.handleChange(event.target.value)
                      }
                    />
                    <PasswordRequirements
                      password={field.state.value}
                      showErrors={isInvalid}
                    />
                    {isInvalid ? (
                      <FieldError errors={field.state.meta.errors} />
                    ) : null}
                  </Field>
                );
              }}
            </form.Field>
            <form.Field name="confirmPassword">
              {(field) => {
                const isInvalid =
                  field.state.meta.isTouched && !field.state.meta.isValid;

                return (
                  <Field data-invalid={isInvalid}>
                    <FieldLabel htmlFor={field.name}>
                      Confirm password
                    </FieldLabel>
                    <PasswordInput
                      id={field.name}
                      name={field.name}
                      aria-invalid={isInvalid}
                      autoComplete="new-password"
                      value={field.state.value}
                      onBlur={field.handleBlur}
                      onChange={(event) =>
                        field.handleChange(event.target.value)
                      }
                    />
                    {isInvalid ? (
                      <FieldError errors={field.state.meta.errors} />
                    ) : null}
                  </Field>
                );
              }}
            </form.Field>
          </FieldGroup>
        </CardContent>
        <CardFooter>
          <form.Subscribe
            selector={(state) => ({
              canSubmit: state.canSubmit,
              isSubmitting: state.isSubmitting,
            })}
          >
            {({ canSubmit, isSubmitting }) => (
              <Button
                type="submit"
                disabled={!canSubmit || isSubmitting || setPassword.isPending}
              >
                {setPassword.isPending ? (
                  <Spinner data-icon="inline-start" />
                ) : (
                  <KeyRound data-icon="inline-start" />
                )}
                {setPassword.isPending ? "Saving..." : "Add password"}
              </Button>
            )}
          </form.Subscribe>
        </CardFooter>
      </form>
    </Card>
  );
}

function LinkedAccountsCard({
  hasPassword,
  linkedProviders,
  onChanged,
}: {
  hasPassword: boolean;
  linkedProviders: string[];
  onChanged: () => void;
}) {
  const [busyProvider, setBusyProvider] = useState<string | null>(null);
  const isGoogleLinked = linkedProviders.includes("google");
  // Never let the user remove their only way to sign in.
  const canUnlinkGoogle = hasPassword;

  async function linkGoogle() {
    setBusyProvider("google");

    const { error } = await authClient.linkSocial({
      callbackURL: getAuthCallbackUrl("/settings/security"),
      provider: "google",
    });

    if (error) {
      toast.error(error.message || "Could not connect Google.");
      setBusyProvider(null);
    }
    // On success the browser redirects to Google — no cleanup needed.
  }

  async function unlinkGoogle() {
    setBusyProvider("google");

    const { error } = await authClient.unlinkAccount({
      providerId: "google",
    });

    setBusyProvider(null);

    if (error) {
      toast.error(error.message || "Could not disconnect Google.");
      return;
    }

    toast.success("Google disconnected");
    onChanged();
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Sign-in methods</CardTitle>
        <CardDescription>
          The ways you can sign in to your account.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-md border p-3">
          <div className="flex items-center gap-2">
            <KeyRound className="size-4 text-muted-foreground" />
            <span className="text-sm">Email & password</span>
            {hasPassword ? (
              <Badge variant="secondary">Active</Badge>
            ) : (
              <Badge variant="outline">Not set</Badge>
            )}
          </div>
          {hasPassword ? null : (
            <span className="text-muted-foreground text-xs">
              Add a password above to enable email sign-in.
            </span>
          )}
        </div>
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-md border p-3">
          <div className="flex items-center gap-2">
            <Link2 className="size-4 text-muted-foreground" />
            <span className="text-sm">Google</span>
            {isGoogleLinked ? (
              <Badge variant="secondary">Connected</Badge>
            ) : (
              <Badge variant="outline">Not connected</Badge>
            )}
          </div>
          {isGoogleLinked ? (
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={busyProvider === "google" || !canUnlinkGoogle}
              title={
                canUnlinkGoogle
                  ? undefined
                  : "Add a password first so you don't lose access to your account."
              }
              onClick={() => {
                void unlinkGoogle();
              }}
            >
              {busyProvider === "google" ? (
                <Spinner data-icon="inline-start" />
              ) : (
                <Unlink data-icon="inline-start" />
              )}
              Disconnect
            </Button>
          ) : (
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={busyProvider === "google"}
              onClick={() => {
                void linkGoogle();
              }}
            >
              {busyProvider === "google" ? (
                <Spinner data-icon="inline-start" />
              ) : (
                <Link2 data-icon="inline-start" />
              )}
              Connect
            </Button>
          )}
        </div>
        {canUnlinkGoogle || !isGoogleLinked ? null : (
          <p className="text-muted-foreground text-xs">
            Google can't be disconnected while it's your only sign-in method.
          </p>
        )}
      </CardContent>
    </Card>
  );
}
